import React from 'react';
import { Box, Grid, MenuItem } from '@mui/material';
import DatePickerContext from 'components/CustomInputsInFormikContext/DatePicker';
import SelectFieldContext from 'components/CustomInputsInFormikContext/Select';
import NumberFormatContext from 'components/CustomInputsInFormikContext/NumberFormat';
import { useFormikContext } from 'formik';
import PartError from 'components/Snack/erroParcela';
import { labelField, roundNumber } from 'utils';
import { sumOfInstallments, sumOfInstallmentsPaid } from '../../helpers';
import { useDependencyInjection } from 'hooks/useDependencyInjection';
import api from 'services/api';

function InputGroup({
  type,
  description,
  index,
  buttons,
  bankAccounts,
  paymentTypes,
}) {
  const {
    id, loadingRequest, success, error: errorServer, requestCompleted,
  } = useDependencyInjection();

  const {
    getFieldMeta, setFieldValue,
  } = useFormikContext();

  const { value: billValue } = getFieldMeta('value');
  const { value: parts } = getFieldMeta('parts');
  const part = parts[index];

  const error = PartError();

  const updateInstallmentValue = (newValue) => {
    const arrayCopy = [...parts];

    if (!arrayCopy[index].paymentDate) {
      const fixedValue = sumOfInstallmentsPaid(arrayCopy);

      const medValue = roundNumber(
        (billValue - fixedValue - newValue)
        / arrayCopy.filter((item, i) => item.paymentDate === null && index !== i).length,
      );

      const mappedArray = arrayCopy.map((item, i) => {
        if (index !== i && item.paymentDate === null) {
          return {
            ...item,
            value: medValue,
          };
        }
        return item;
      });

      const newItemValue = Number((billValue - sumOfInstallments(mappedArray)).toFixed(2));
      mappedArray[mappedArray.length - 1].value += newItemValue;

      const sumOfInstallmentsExceptCurrentOne = parts.reduce((acc, curr, i) => (index !== i ? acc + curr.value : acc), 0);
      const oldValue = Math.abs(billValue - sumOfInstallmentsExceptCurrentOne);

      if (Number(newValue.toFixed(2)) > Number(billValue.toFixed(2))) {
        error();
        setFieldValue(`parts[${index}].value`, oldValue);
        return;
      }

      setFieldValue('parts', [...mappedArray]);
    }
  };

  const disabledField = id && part.pay;

  async function request(data) {
    const mappedData = { ...data, type, description };
    try {
      loadingRequest();
      if (data.id) {
        await api.put(`/bill-part/${data.id}`, { ...mappedData });
      } else {
        await api.post('/bill-part', { ...mappedData });
      }
      success();
    } catch (err) {
      errorServer();
    } finally {
      requestCompleted();
    }
  }

  return (
    <Box display="flex" gap={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={2.4}>
          <DatePickerContext
            label="Data de Vencimento"
            name={`parts.${index}.expirationDate`}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2.4}>
          <DatePickerContext
            label="Data de Pagamento"
            name={`parts.${index}.paymentDate`}
            maxDate={new Date()}
            onChangeCallback={(date) => {
              if (date) {
                setFieldValue(`parts.${index}.pay`, true);
                if (id) {
                  request({ ...part, paymentDate: date, pay: true });
                }
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md lg={2.4}>
          <SelectFieldContext
            label={labelField('Forma de Pagamento')}
            name={`parts.${index}.paymentType`}
          >
            {paymentTypes?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.description}
              </MenuItem>
            ))}
          </SelectFieldContext>
        </Grid>
        <Grid item xs={12} md lg={2.4}>
          <SelectFieldContext
            label={labelField('Conta de recebimento')}
            name={`parts.${index}.bankAccount`}
          >
            {bankAccounts?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </SelectFieldContext>
        </Grid>
        <Grid item xs={12} md lg={2.4}>
          <NumberFormatContext
            disabled={disabledField}
            fieldSize={20}
            label="Total da parcela"
            name={`parts.${index}.value`}
            prefix="R$ "
            decimalScale={2}
            onValueChangeCallback={(value) => {
              if (!value) {
                setFieldValue(`parts.${index}.value`, 0);
              }
            }}
            onBlurCallback={() => updateInstallmentValue(Number(part.value))}
          />
        </Grid>
      </Grid>
      {buttons && (
        <Grid
          item
          xs="auto"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          buttons
        </Grid>
      )}
    </Box>
  );
}

export default InputGroup;
